'use client'

import { useState } from 'react'

import { XMarkIcon } from '@heroicons/react/24/outline'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import Image from 'next/image'

import { SplitsLink } from '~/components/Link'
import { Button } from '~/components/component-library'
import thumbnail from '~/public/demo-video-thumbnail-v3.png'

const BOOKING_URL = 'https://book.vimcal.com/p/abram/a1d5'
const DEMO_VIDEO_ID = '8rqZxfmubho'
const VIDEO_DURATION = '4:53'

function PlayButton() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="flex items-center gap-1.5 rounded-full bg-black/70 py-2 pl-3.5 pr-4 shadow-lg backdrop-blur-sm transition-colors duration-200 group-hover:bg-black/80">
        <svg
          viewBox="0 0 24 24"
          fill="white"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M8 5v14l11-7z" />
        </svg>
        <span className="text-sm font-medium text-white">Watch demo</span>
      </div>
    </div>
  )
}

function VideoOverlay({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:p-8">
          <DialogPrimitive.Content
            className="relative w-full max-w-4xl"
            aria-describedby={undefined}
          >
            <VisuallyHidden>
              <DialogPrimitive.Title>Demo video</DialogPrimitive.Title>
            </VisuallyHidden>
            <DialogPrimitive.Close className="absolute -right-2 -top-10 rounded-lg p-2 text-white/80 transition-colors hover:text-white">
              <XMarkIcon className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}?autoplay=1&rel=0`}
                allow="autoplay; encrypted-media"
                allowFullScreen
                className="h-full w-full rounded-lg"
                title="Splits demo"
              />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export function DesktopDemoVideoThumbnail() {
  const [videoOpen, setVideoOpen] = useState(false)

  return (
    <>
      <div>
        <div
          className="group cursor-pointer overflow-hidden rounded-md shadow-lg shadow-black/10 transition-transform duration-200 hover:scale-[1.01]"
          onClick={() => setVideoOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setVideoOpen(true)
            }
          }}
          aria-label="Play demo video"
        >
          <div className="relative aspect-video">
            <Image
              src={thumbnail}
              alt="Splits demo video"
              fill
              placeholder="blur"
              className="object-cover"
              sizes="384px"
            />
            <PlayButton />
            <span className="absolute bottom-1.5 right-1.5 rounded-sm bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
              {VIDEO_DURATION}
            </span>
          </div>
        </div>
        <p className="mt-3 text-center text-sm text-secondary">
          {'or '}
          <SplitsLink path={BOOKING_URL} target="tab" underline>
            book a call
          </SplitsLink>
          {' or '}
          <SplitsLink path="https://splits.org/teams/" target="tab" underline>
            learn more
          </SplitsLink>
        </p>
      </div>
      <VideoOverlay open={videoOpen} onOpenChange={setVideoOpen} />
    </>
  )
}

export function MobileDemoVideoThumbnail() {
  const [videoOpen, setVideoOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  return (
    <>
      <div
        className="fixed bottom-4 left-4 z-10 w-40 cursor-pointer rounded-md shadow-lg shadow-black/10"
        onClick={() => setVideoOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setVideoOpen(true)
          }
        }}
        aria-label="Play demo video"
      >
        <div className="relative aspect-video">
          <Image
            src={thumbnail}
            alt="Splits demo video"
            fill
            placeholder="blur"
            className="object-cover"
            sizes="160px"
          />
          <PlayButton />
          <span className="absolute bottom-1 right-1 rounded-sm bg-black/70 px-1 py-0.5 text-[10px] font-medium text-white">
            {VIDEO_DURATION}
          </span>
          <Button
            className="absolute right-1 top-1 z-20 size-5 rounded-full bg-black/60 p-0.5 text-white/80 transition-colors hover:text-white"
            onClick={(e) => {
              e.stopPropagation()
              setDismissed(true)
            }}
            aria-label="Dismiss video thumbnail"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <VideoOverlay open={videoOpen} onOpenChange={setVideoOpen} />
    </>
  )
}
